import axios from 'axios';
import { API_URL } from '../config';
import { AxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  permission?: string;
}

const getPermissionConfig = (
  permission?: string | null,
  signal?: AbortSignal | null,
): CustomAxiosRequestConfig => {
  const config: CustomAxiosRequestConfig = {};

  if (signal) {
    config.signal = signal;
  }

  if (permission) {
    config.permission = permission;
  }

  return config;
};

export interface WorkingAreaPayload {
  name: string;
  address?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  radius?: number | null;
  valid_from: string;
  valid_to?: string | null;
  justification?: number | null;
  nfc_tag?: string | null;
  notes?: string | null;
}

export interface WorkingAreaTimeSlotPayload {
  contract: number;
  time_slot: number;
  working_area: number;
  date: string;
}

export interface WorkingAreaTimeSlot {
  id: number;
  employee: number;
  contract: number;
  time_slot: number;
  working_area: number;
  working_area_name?: string | null;
  working_area_code?: string | null;
  time_slot_start?: number | null;
  time_slot_end?: number | null;
  date: string;
}

export interface WorkingAreaPresenceSummary {
  id: number;
  name: string;
  total_checkins: number;
  total_checkouts: number;
  presences_now: number;
}

export interface WorkingAreaPresenceListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkingAreaPresenceSummary[];

  total_checkins: number;
  total_checkouts: number;
  presences_now: number;
}

export interface WorkingAreaPresenceDetail {
  id: number;
  contract_id: number;
  cf: string;
  first_name?: string;
  last_name?: string;
  checkin: string;
  checkout: string | null;
}

export interface WorkingAreaPresenceDetailResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkingAreaPresenceDetail[];
}

export interface WorkingAreaTimeSlotBulkPayload {
  contract: number;
  working_area: number;
  date: string;
  time_slots: number[];
}

export const getWorkingAreaList = async (
  token: string,
  params?: {
    status?: "all" | "active" | "inactive";
    search?: string;
    date?: string;
    page?: number;
    page_size?: number;
    nfc_available?: boolean;
    nfc_tag?: string | number;
  },
  permission?: string | null,
) => {
  const config = getPermissionConfig(permission);

  const response = await axios.get(`${API_URL}/presenze/company/working-areas/`, {
    headers: {
      'Authorization': token,
    },
    params,
    ...config,
  });

  return response.data;
};


export const getAvailableWorkingAreaList = async (token: string, nfcTagId: string, permission?: string | null,) => {
  const config = getPermissionConfig(permission);

  const response = await axios.get(`${API_URL}/presenze/company/working-areas/`, {
    headers: {
      'Authorization': token,
    },
    params: {
      nfc_available: true,
      nfc_tag: nfcTagId,
    },
    ...config,
  });

  return response.data;
};

export const getWorkingAreaId = async (token: string, id: string | number, permission: string | null) => {
  const config = getPermissionConfig(permission);

  const response = await axios.get(`${API_URL}/presenze/company/working-areas/${id}/`, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};


export const createWorkingArea = async (token: string, workingAreaData: WorkingAreaPayload, permission?: string | null,) => {
  const config = getPermissionConfig(permission);

  const response = await axios.post(`${API_URL}/presenze/company/working-areas/`,
    workingAreaData,
    {
      headers: {
        'Authorization': token,
      },
      ...config,
    },
  );

  return response.data;
};


export const updateWorkingArea = async (token: string, id: string | number, workingAreaData: WorkingAreaPayload, permission: string | null) => {
  const config = getPermissionConfig(permission);

  const response = await axios.put(`${API_URL}/presenze/company/working-areas/${id}/`,
    workingAreaData,
    {
      headers: {
        'Authorization': token,
      },
      ...config,
    }
  );

  return response.data;
};


export const patchWorkingArea = async (token: string, id: string | number, workingAreaData: Partial<WorkingAreaPayload>, permission?: string | null,) => {
  const config = getPermissionConfig(permission);

  const response = await axios.patch(`${API_URL}/presenze/company/working-areas/${id}/`,
    workingAreaData,
    {
      headers: {
        'Authorization': token,
      },
      ...config,
    }
  );

  return response.data;
};


export const deleteWorkingArea = async (token: string, id: string | number, permission: string | null,) => {
  const config = getPermissionConfig(permission);

  const response = await axios.delete(`${API_URL}/presenze/company/working-areas/${id}/`, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};


export const getWorkingAreaTimeSlotList = async (
  token: string,
  params?: {
    employee?: number;
    contract?: number;
    time_slot?: number;
    working_area?: number;
    date?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    page_size?: number;
    paginate?: boolean;
  },
  signal?: AbortSignal,
  permission?: string | null,
) => {
  const config = getPermissionConfig(permission, signal);

  const response = await axios.get(`${API_URL}/presenze/company/working-area-time-slots/`, {
      headers: {
        'Authorization': token,
      },
      params,
      ...config,
    },
  );

  return response.data;
};

export const createWorkingAreaTimeSlot = async ( token: string, data: WorkingAreaTimeSlotPayload, permission: string | null) => {
  const config = getPermissionConfig(permission);

  const response = await axios.post(`${API_URL}/presenze/company/working-area-time-slots/`, data, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};

export const getWorkingAreaTimeSlot = async ( token: string, id: string | number, permission: string | null ) => {
  const config = getPermissionConfig(permission);

  const response = await axios.get(`${API_URL}/presenze/company/working-area-time-slots/${id}/`, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};

export const patchWorkingAreaTimeSlot = async ( token: string, id: string | number, data: Partial<WorkingAreaTimeSlotPayload>, permission: string | null) => {
  const config = getPermissionConfig(permission);

  const response = await axios.patch(`${API_URL}/presenze/company/working-area-time-slots/${id}/`, data, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};

export const deleteWorkingAreaTimeSlot = async ( token: string, id: string | number, permission: string | null ) => {
  const config = getPermissionConfig(permission);

  const response = await axios.delete(`${API_URL}/presenze/company/working-area-time-slots/${id}/`, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};

export const bulkUpsertWorkingAreaTimeSlots = async ( token: string, data: WorkingAreaTimeSlotBulkPayload, permission: string | null ) => {
  const config = getPermissionConfig(permission);

  const response = await axios.post(`${API_URL}/presenze/company/working-area-time-slots/bulk-upsert/`, data, {
    headers: {
      'Authorization': token,
    },
    ...config,
  });

  return response.data;
};

export const bulkDeleteWorkingAreaTimeSlots = async (token: string, ids: number[], permission: string | null) => {
  const config = getPermissionConfig(permission);

  const response = await axios.delete(`${API_URL}/presenze/company/working-area-time-slots/bulk-delete/`, {
    headers: {
      'Authorization': token,
    },
    data: {ids,},
    ...config,
  });

  return response.data;
};

export const getWorkingAreaPresences = async (
  token: string,
  params?: {
    working_area?: number;
    page?: number;
    page_size?: number;
  },
  signal?: AbortSignal,
  permission?: string | null,
): Promise<WorkingAreaPresenceListResponse> => {
  const config = getPermissionConfig(permission, signal);

  const response = await axios.get(`${API_URL}/presenze/company/working-area-presences/`, {
    headers: {
      'Authorization': token,
    },
    params,
    ...config,
  });

  return response.data;
};

export const getWorkingAreaPresenceDetail = async (
  token: string,
  workingAreaId: number,
  params?: {
    page?: number;
    page_size?: number;
    status?: "all" | "in" | "out";
  },
  signal?: AbortSignal,
  permission?: string | null,
): Promise<WorkingAreaPresenceDetailResponse> => {
  const config = getPermissionConfig(permission, signal);

  const response = await axios.get(`${API_URL}/presenze/company/working-area-presences/${workingAreaId}/`,
    {
      headers: {
        'Authorization': token,
      },
      params,
      ...config,
    }
  );

  return response.data;
};


export const bulkAssignWorkingAreaRange = async (
  token: string,
  payload: any,
  permission: string | null = 'shifts'
) => {
  const config = getPermissionConfig(permission);

  const response = await axios.post(
    `${API_URL}/presenze/company/working-area-time-slots/bulk-range/`,
    payload,
    {
      headers: {
        'Authorization': token,
      },
      ...config,
    }
  );

  return response.data;
};

